import { useEffect, useState } from 'react'

/**
 * useCountdown — derives a live, human-formatted countdown from an
 * ISO 8601 target.
 *
 * Drives the AccountLockedScreen's lockout countdown ("Try again in
 * 23 min 47 sec"). Returns:
 *
 *   - totalSeconds: max(0, (target - now) / 1000) — never negative
 *   - formatted:    "X min Y sec" when > 60s, "Y sec" when < 60s,
 *                   "Now" once the target has passed (or when null)
 *   - expired:      true once totalSeconds hits 0
 *
 * A null/undefined target short-circuits to the expired terminal state
 * so consumers can pass `error.envelope.locked_until ?? null` directly
 * without a wrapper conditional. The setInterval is torn down on
 * unmount; without that guarantee a user navigating away from the
 * locked screen would leak a 1Hz tick forever.
 */
export interface UseCountdownResult {
  totalSeconds: number
  formatted: string
  expired: boolean
}

const TERMINAL: UseCountdownResult = {
  totalSeconds: 0,
  formatted: 'Now',
  expired: true,
}

function formatRemaining(seconds: number): string {
  if (seconds <= 0) return 'Now'
  if (seconds < 60) return `${seconds} sec`
  const min = Math.floor(seconds / 60)
  const sec = seconds % 60
  return `${min} min ${sec} sec`
}

function computeFromTarget(targetISO: string): UseCountdownResult {
  const targetMs = Date.parse(targetISO)
  if (Number.isNaN(targetMs)) return TERMINAL
  const deltaSec = Math.max(0, Math.floor((targetMs - Date.now()) / 1000))
  return {
    totalSeconds: deltaSec,
    formatted: formatRemaining(deltaSec),
    expired: deltaSec === 0,
  }
}

export function useCountdown(targetISO: string | null | undefined): UseCountdownResult {
  // Treat nullish + unparseable input as terminal. The hook returns a
  // stable object so callers can `useMemo` downstream without churn.
  const [state, setState] = useState<UseCountdownResult>(() => {
    if (!targetISO) return TERMINAL
    return computeFromTarget(targetISO)
  })

  useEffect(() => {
    if (!targetISO) {
      setState(TERMINAL)
      return
    }
    // Recompute synchronously on target change so a fresh mount doesn't
    // wait for the first tick to render.
    setState(computeFromTarget(targetISO))

    const id = setInterval(() => {
      setState((prev) => {
        const next = computeFromTarget(targetISO)
        // Avoid re-rendering when nothing observable changed (most of
        // the 1Hz ticks land in the same second bucket after rounding).
        if (
          prev.totalSeconds === next.totalSeconds &&
          prev.formatted === next.formatted &&
          prev.expired === next.expired
        ) {
          return prev
        }
        return next
      })
    }, 1000)

    return () => {
      clearInterval(id)
    }
  }, [targetISO])

  return state
}
